/**
 * The arithmetic of moving over a surface and zooming into it.
 *
 * **Pure functions and no React**, deliberately, and that is why they live in a file of their own: a
 * board is drawn with one CSS transform, and everything that is hard about it — turning a pointer
 * into a place on the board, zooming about the point somebody is looking at, fitting a whole board
 * into a pane — is arithmetic that can be checked without a DOM. What is left in `Canvas.tsx` is the
 * part that genuinely needs pointers and elements.
 *
 * The transform is `translate(x, y) scale(k)` with `transform-origin: 0 0`, so a point on the board
 * lands on the screen at `board * k + offset`. Every function here is that one line read forwards or
 * backwards.
 */

export interface Viewport {
  /** Where the board's origin lies on the screen, in screen pixels from the pane's top left. */
  readonly x: number
  readonly y: number
  readonly scale: number
}

export interface Point {
  readonly x: number
  readonly y: number
}

export interface Rect {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

export interface Size {
  readonly width: number
  readonly height: number
}

/**
 * How far the surface may be zoomed.
 *
 * Not a matter of taste at either end. Below the lower bound a card is a coloured speck and the
 * board has stopped being readable; above the upper one a first page is a wall of blurred pixels,
 * because a preview is a picture and not a vector.
 */
export const MIN_SCALE = 0.2
export const MAX_SCALE = 3

/** The viewport a board starts at: origin at the top left, nothing zoomed. */
export const AT_REST: Viewport = { x: 0, y: 0, scale: 1 }

export function clampScale(scale: number): number {
  if (!Number.isFinite(scale)) {
    return 1
  }
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale))
}

/** Where a point on the screen lies on the board. */
export function toBoard(viewport: Viewport, screen: Point): Point {
  return {
    x: (screen.x - viewport.x) / viewport.scale,
    y: (screen.y - viewport.y) / viewport.scale,
  }
}

/** And back: where a point on the board lands on the screen. */
export function toScreen(viewport: Viewport, board: Point): Point {
  return {
    x: board.x * viewport.scale + viewport.x,
    y: board.y * viewport.scale + viewport.y,
  }
}

/** The same viewport, moved by a distance in screen pixels. */
export function pannedBy(viewport: Viewport, dx: number, dy: number): Viewport {
  return { ...viewport, x: viewport.x + dx, y: viewport.y + dy }
}

/**
 * Zooms about a point on the screen, leaving that point where it is.
 *
 * That is the whole of what makes zooming feel like zooming rather than like jumping: the thing
 * under the pointer — or between two fingers — stays under it. Everything else follows from solving
 * `board * k + offset = screen` for the new offset.
 */
export function zoomAround(viewport: Viewport, screen: Point, factor: number): Viewport {
  const scale = clampScale(viewport.scale * factor)
  const at = toBoard(viewport, screen)
  return {
    scale,
    x: screen.x - at.x * scale,
    y: screen.y - at.y * scale,
  }
}

/** Zooms to an exact factor about the middle of the pane — what the `+` and `−` controls do. */
export function zoomTo(viewport: Viewport, pane: Size, scale: number): Viewport {
  const middle = { x: pane.width / 2, y: pane.height / 2 }
  return zoomAround(viewport, middle, clampScale(scale) / viewport.scale)
}

/** The smallest rectangle holding all of them, or `null` when there are none. */
export function boundsOf(rects: readonly Rect[]): Rect | null {
  if (rects.length === 0) {
    return null
  }
  const left = Math.min(...rects.map((one) => one.x))
  const top = Math.min(...rects.map((one) => one.y))
  const right = Math.max(...rects.map((one) => one.x + one.width))
  const bottom = Math.max(...rects.map((one) => one.y + one.height))
  return { x: left, y: top, width: right - left, height: bottom - top }
}

/**
 * The viewport that brings everything into the pane at once — "fit all".
 *
 * One of the two ways back on a full board, and the reason there is no overview map: a map would be
 * a second, smaller board with the same problem on it.
 *
 * An empty board is left exactly as it was. There is nothing to fit, and jumping the surface to
 * somewhere arbitrary would be the one thing worse than doing nothing.
 */
export function fitAll(rects: readonly Rect[], pane: Size, padding = 48): Viewport {
  const bounds = boundsOf(rects)
  if (bounds === null || pane.width <= 0 || pane.height <= 0) {
    return AT_REST
  }
  return fitTo(bounds, pane, padding)
}

/** The viewport that puts one rectangle in the middle of the pane, at a readable size. */
export function fitTo(rect: Rect, pane: Size, padding = 48): Viewport {
  const room = {
    width: Math.max(1, pane.width - padding * 2),
    height: Math.max(1, pane.height - padding * 2),
  }
  const scale = clampScale(
    Math.min(room.width / Math.max(rect.width, 1), room.height / Math.max(rect.height, 1)),
  )
  return {
    scale,
    x: pane.width / 2 - (rect.x + rect.width / 2) * scale,
    y: pane.height / 2 - (rect.y + rect.height / 2) * scale,
  }
}

/**
 * Brings one rectangle into view **without changing the zoom** — what jumping to a search hit does.
 *
 * The zoom is somebody's own setting, and a jump that also rescaled the board would answer "where is
 * it?" by rearranging everything else. Something already in view is left alone entirely, so pressing
 * the same hit twice does not shuffle the surface.
 */
export function bringIntoView(viewport: Viewport, rect: Rect, pane: Size, margin = 80): Viewport {
  const topLeft = toScreen(viewport, rect)
  const size = { width: rect.width * viewport.scale, height: rect.height * viewport.scale }

  const inView =
    topLeft.x >= margin &&
    topLeft.y >= margin &&
    topLeft.x + size.width <= pane.width - margin &&
    topLeft.y + size.height <= pane.height - margin
  if (inView) {
    return viewport
  }

  return {
    scale: viewport.scale,
    x: pane.width / 2 - (rect.x + rect.width / 2) * viewport.scale,
    y: pane.height / 2 - (rect.y + rect.height / 2) * viewport.scale,
  }
}

/** Whether the two rectangles touch at all — how a frame decides what it is carrying. */
export function holds(frame: Rect, other: Rect): boolean {
  return (
    other.x >= frame.x &&
    other.y >= frame.y &&
    other.x + other.width <= frame.x + frame.width &&
    other.y + other.height <= frame.y + frame.height
  )
}

/** The middle of a rectangle. */
export function middleOf(rect: Rect): Point {
  return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 }
}
